import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function PotentialEnergyCalculator() {
  const [mass, setMass] = useState("");
  const [height, setHeight] = useState("");
  const [gravity, setGravity] = useState("9.81");
  const [result, setResult] = useState<number | null>(null);

  const calculatePotentialEnergy = () => {
    const m = parseFloat(mass);
    const h = parseFloat(height);
    const g = parseFloat(gravity);

    if (isNaN(m) || isNaN(h) || isNaN(g)) {
      toast.error("Please enter valid values");
      return;
    }

    const pe = m * g * h;
    setResult(pe);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setHeight("");
    setGravity("9.81");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Potential Energy Calculator" 
      calculatorPath="/calculator/potential-energy"
      category="physics"
      subcategory="Energy"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Potential Energy Calculator</h1>
          <p className="text-muted-foreground">
            Calculate gravitational potential energy
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: PE = mgh</CardTitle>
            <CardDescription>
              Where PE is potential energy (J), m is mass (kg), g is gravity (m/s²), and h is height (m)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (kg)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="0.01"
                  placeholder="Enter mass"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gravity">Gravity (m/s²)</Label>
                <Input
                  id="gravity"
                  type="number"
                  step="0.01"
                  placeholder="Enter gravity"
                  value={gravity}
                  onChange={(e) => setGravity(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="height">Height (m)</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.01"
                  placeholder="Enter height"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculatePotentialEnergy} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} J
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Potential Energy (Joules)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["potential-energy"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the mass of the object in kilograms</p>
                <p>2. Use default gravity (9.81 m/s²) or enter custom value</p>
                <p>3. Enter the height above reference point in meters</p>
                <p>4. Click Calculate to get potential energy in Joules</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 5 kg object at 10 m height = 490.5 J potential energy
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
